import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_USERS,
  CREATE_USER,
  UPDATE_USER,
  DELETE_USER,
} from "../graphql/users";
import { useLogger } from "../services/logging";
import { User } from "../types/user";
import { DataGrid, GridColDef, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import { FeeSettingsDialog } from "./FeeSettingsDialog";

interface UserFormState {
  id?: string;
  email: string;
  pushbulletKey?: string;
  phoneNumber?: string;
  telegramId?: string;
  notificationPreferences?: string;
}

const defaultForm: UserFormState = {
  email: "",
  pushbulletKey: "",
  phoneNumber: "",
  telegramId: "",
  notificationPreferences: "",
};

export function UserManagement() {
  const logger = useLogger("UserManagement");
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<UserFormState>(defaultForm);
  const [isEditing, setIsEditing] = useState(false);
  const [feeSettingsOpen, setFeeSettingsOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_USERS);
  const [createUser] = useMutation(CREATE_USER);
  const [updateUser] = useMutation(UPDATE_USER);
  const [deleteUser] = useMutation(DELETE_USER);

  const handleSubmit = async () => {
    try {
      if (isEditing) {
        await updateUser({
          variables: {
            id: formData.id,
            input: {
              email: formData.email,
              pushbulletKey: formData.pushbulletKey || null,
              phoneNumber: formData.phoneNumber || null,
              telegramId: formData.telegramId || null,
              notificationPreferences: formData.notificationPreferences || null,
            },
          },
        });
      } else {
        await createUser({
          variables: {
            input: {
              email: formData.email,
              pushbulletKey: formData.pushbulletKey || null,
              phoneNumber: formData.phoneNumber || null,
              telegramId: formData.telegramId || null,
              notificationPreferences: formData.notificationPreferences || null,
            },
          },
        });
      }
      refetch();
      handleClose();
    } catch (error) {
      logger.error("Error saving user", { method: "handleSave" }, error);
    }
  };

  const handleEdit = (user: User) => {
    setFormData({
      id: user.id,
      email: user.email,
      pushbulletKey: user.pushbulletKey || "",
      phoneNumber: user.phoneNumber || "",
      telegramId: user.telegramId || "",
      notificationPreferences: user.notificationPreferences || "",
    });
    setIsEditing(true);
    setOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await deleteUser({ variables: { id } });
        refetch();
      } catch (error) {
        logger.error("Error deleting user", { method: "handleDelete" }, error);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(defaultForm);
    setIsEditing(false);
  };

  const handleFeeSettingsClose = () => {
    setFeeSettingsOpen(false);
  };

  const handleAdd = () => {
    setFormData(defaultForm);
    setIsEditing(false);
    setOpen(true);
  };

  const columns: GridColDef[] = [
    { field: "email", headerName: "Email", width: 200 },
    { field: "pushbulletKey", headerName: "Pushbullet Key", width: 150 },
    { field: "phoneNumber", headerName: "Phone Number", width: 150 },
    { field: "telegramId", headerName: "Telegram ID", width: 150 },
    {
      field: "notificationPreferences",
      headerName: "Notification Preferences",
      width: 200,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ];

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <Box sx={{ height: 400, width: "100%" }}>
      <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Add User
        </Button>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setFeeSettingsOpen(true)}
        >
          Fee Settings
        </Button>
      </Box>

      <DataGrid
        rows={data?.users || []}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        pageSizeOptions={[5, 10, 25, 50]}
        checkboxSelection
        disableRowSelectionOnClick
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? "Edit User" : "Add User"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Pushbullet Key"
            fullWidth
            variant="outlined"
            value={formData.pushbulletKey}
            onChange={(e) =>
              setFormData({ ...formData, pushbulletKey: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Phone Number"
            fullWidth
            variant="outlined"
            value={formData.phoneNumber}
            onChange={(e) =>
              setFormData({ ...formData, phoneNumber: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Telegram ID"
            fullWidth
            variant="outlined"
            value={formData.telegramId}
            onChange={(e) =>
              setFormData({ ...formData, telegramId: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Notification Preferences"
            fullWidth
            variant="outlined"
            value={formData.notificationPreferences}
            onChange={(e) =>
              setFormData({
                ...formData,
                notificationPreferences: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <FeeSettingsDialog
        open={feeSettingsOpen}
        onClose={handleFeeSettingsClose}
        onSaved={() => {
          refetch();
          handleFeeSettingsClose();
        }}
      />
    </Box>
  );
}
